import { useEffect, useMemo, useState } from 'react';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import envConfig from '../../../envConfig';

const getTierYearlyPrice = (tier) => {
  if (!tier) return 0;

  const yearlyPrice = parseFloat(tier.yearlyPrice);
  if (!Number.isNaN(yearlyPrice)) return yearlyPrice;

  const annualPrice = parseFloat(tier.annualPrice);
  if (!Number.isNaN(annualPrice)) return annualPrice;

  return 0;
};

const getLowestPaidTier = (tiers) => tiers
  .filter((tier) => getTierYearlyPrice(tier) > 0)
  .sort((a, b) => getTierYearlyPrice(a) - getTierYearlyPrice(b))[0];

function useServicePlanTiers({ enabled = true } = {}) {
  const [tiers, setTiers] = useState([]);
  const [tiersLoading, setTiersLoading] = useState(false);
  const [tiersError, setTiersError] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    if (tiers.length) return undefined;

    let cancelled = false;

    async function fetchTiers() {
      try {
        setTiersLoading(true);
        setTiersError(null);

        const res = await RaaAuthenticatedRemoteService.globalInstance()
          .get(`${envConfig.apiUrl}/api/v1/billing/public/subscription-tier`);

        if (!cancelled) {
          setTiers((res && res.tiers) ? res.tiers : []);
        }
      } catch (err) {
        if (!cancelled) setTiersError(err.message || 'Failed to load plans');
      } finally {
        if (!cancelled) setTiersLoading(false);
      }
    }

    fetchTiers();

    return () => {
      cancelled = true;
    };
  }, [enabled, tiers.length]);

  const lowestPaidTier = useMemo(() => getLowestPaidTier(tiers), [tiers]);

  return {
    tiers,
    tiersLoading,
    tiersError,
    getTierYearlyPrice,
    lowestPaidTier,
  };
}

export default useServicePlanTiers;
